
import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import WebsiteLinksInput from './components/WebsiteLinksInput';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';

const ModernResumeForm = ({ resumeData, onUpdate, validationErrors = {}, onFieldFocus, onFieldBlur, getDynamicSections, renderDynamicSectionForm }) => {
  const [activeSection, setActiveSection] = useState('contact');
  const [isUploading, setIsUploading] = useState(false);
  const [newWebsite, setNewWebsite] = useState("");




  const updateField = (field, data) => {
    onUpdate(prev => ({ ...prev, [field]: data }));
  };

  const handlePersonalChange = (field, value) => {
    onUpdate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      
      // Simple fetch request to upload file
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        handlePersonalChange('profile_image_url', result.file_url || URL.createObjectURL(file));
      } else {
        // Fallback to local preview if upload fails
        handlePersonalChange('profile_image_url', URL.createObjectURL(file));
      }
    } catch (error) {
      // Fallback to local preview
      handlePersonalChange('profile_image_url', URL.createObjectURL(file));
    }
    setIsUploading(false);
  };

  const addExperience = () => {
    updateField("experience", [...(resumeData.experience || []), {
      position: "",
      company: "",
      location: "",
      start_date: "",
      end_date: "",
      description_points: []
    }]);
  };

  const getDescriptionPoints = (exp) =>
    Array.isArray(exp?.description_points) ? exp.description_points : [];

  const updateExperience = (index, field, value) => {
    const updated = [...(resumeData.experience || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("experience", updated);
  };

  const addDescriptionPoint = (expIndex) => {
    const updated = [...(resumeData.experience || [])];
    const current = { ...updated[expIndex] };
    const points = [...getDescriptionPoints(current), ""];
    current.description_points = points;
    updated[expIndex] = current;
    updateField("experience", updated);
  };

  const updateDescriptionPoint = (expIndex, pointIndex, value) => {
    const updated = [...(resumeData.experience || [])];
    const current = { ...updated[expIndex] };
    const points = getDescriptionPoints(current).map((point, idx) =>
      idx === pointIndex ? value : point
    );
    current.description_points = points;
    updated[expIndex] = current;
    updateField("experience", updated);
  };

  const removeDescriptionPoint = (expIndex, pointIndex) => {
    const updated = [...(resumeData.experience || [])];
    const current = { ...updated[expIndex] };
    const points = getDescriptionPoints(current).filter((_, idx) => idx !== pointIndex);
    current.description_points = points;
    updated[expIndex] = current;
    updateField("experience", updated);
  };

  const removeExperience = (index) => {
    updateField("experience", resumeData.experience.filter((_, i) => i !== index));
  };
  
  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "",
      field_of_study: "",
      institution: "",
      location: "",
      start_date: "",
      start_year: "",
      graduation_date: "",
      end_date: "",
      end_year: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    const current = { ...updated[index], [field]: value };
    if (field === "start_date") {
      current.start_year = value;
    }
    if (field === "start_year") {
      current.start_date = value;
    }
    if (field === "graduation_date" || field === "end_date") {
      current.graduation_date = value;
      current.end_date = value;
      current.end_year = value;
    }
    if (field === "end_year") {
      current.graduation_date = value;
      current.end_date = value;
    }
    updated[index] = current;
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    const list = Array.isArray(resumeData.education) ? resumeData.education : [];
    updateField("education", list.filter((_, i) => i !== index));
  };

  const addReference = () => {
    updateField("references", [...(resumeData.references || []), {
      name: "",
      title: "",
      company: "",
      phone: "",
      email: ""
    }]);
  };

  const updateReference = (index, field, value) => {
    const updated = [...(resumeData.references || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("references", updated);
  };

  const removeReference = (index) => {
    updateField("references", resumeData.references.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };
  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };
  const addProject = () => {
    updateField("projects", [...(resumeData.projects || []), {
        title: "",
        name: "", // Keep for compatibility
        description: "",
        link: "",
        year: "",
        technologies: []
    }]);
  };
  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };
  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    // Maintain data consistency across templates
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'tech_stack') {
      updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    if (field === 'github_link') {
      updated[index].link = value;
    }
    
    updateField("projects", updated);
  };

  const addAchievement = () => {
    updateField("achievements", [...(resumeData.achievements || []), ""]);
  };

  const updateAchievement = (index, value) => {
    const updated = [...(resumeData.achievements || [])];
    updated[index] = value;
    updateField("achievements", updated);
  };

  const removeAchievement = (index) => {
    updateField("achievements", resumeData.achievements.filter((_, i) => i !== index));
  };

  const addCertification = () => {
    updateField("certifications", [...(resumeData.certifications || []), { name: "", provider: "" }]);
  };

  const updateCertification = (index, field, value) => {
    const updated = [...(resumeData.certifications || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("certifications", updated);
  };

  const removeCertification = (index) => {
    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
  };

  const baseSections = [
    { id: 'contact', label: 'Contact' },
    { id: 'summary', label: 'Summary' },
    { id: 'experience', label: 'Experience' },
    { id: 'skills', label: 'Skills' },
    { id: 'education', label: 'Education' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'certifications', label: 'Certifications' },
    // { id: 'languages', label: 'Languages' }, // Removed - comes from dynamic sections
    { id: 'references', label: 'References' },
    { id: 'projects', label: 'Projects' }
  ];

  // Combine base sections with dynamic sections from ResumeEditor
  const sections = getDynamicSections ? [...baseSections, ...getDynamicSections()] : baseSections;

  return (
    <div className="resume-form" style={{marginTop: 0, paddingTop: 0}}>
      <div className="form-content" style={{marginTop: 0, paddingTop: 0}}>
        <div className="form-header">
          <h2 className="form-title">Modern Resume Editor</h2>
        </div>
        <SectionSelector
          sections={sections}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
        <div className="section-form">
          {activeSection === 'contact' && (
            <div className="tab-panel">
              <h3 className="section-title">Contact Information</h3>
              
              <div className={`form-group ${validationErrors.profile_image_url ? 'error' : ''}`}>
                <label>Profile Picture</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  {resumeData?.profile_image_url && (
                    <img 
                      src={resumeData.profile_image_url} 
                      alt="Profile Preview" 
                      style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #ddd' }}
                    />
                  )}
                  <div>
                    <input
                      type="file"
                      id="profile-image-upload"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                    <label htmlFor="profile-image-upload" className="add-button" style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.7 : 1 }}>
                      {isUploading ? 'Uploading...' : 'Upload Photo'}
                    </label>
                    {resumeData?.profile_image_url && (
                       <button 
                         type="button"
                         onClick={() => handlePersonalChange('profile_image_url', '')} 
                         className="remove-button" 
                         style={{marginLeft: '10px'}}
                       >
                         🗑️ Remove
                       </button>
                    )}
                  </div>
                </div>
                {validationErrors.profile_image_url && <p className="error-message">{validationErrors.profile_image_url}</p>}
              </div>

              <div className="form-group-grid">
                <div className={`form-group ${validationErrors.full_name ? 'error' : ''}`}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={resumeData?.full_name || ""}
                    onChange={(e) => handlePersonalChange("full_name", e.target.value)}
                    placeholder="Haitham Mohamed"
                  />
                  {validationErrors.full_name && <p className="error-message">{validationErrors.full_name}</p>}
                </div>
                <div className={`form-group ${validationErrors.job_title ? 'error' : ''}`}>
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={resumeData?.job_title || ""}
                    onChange={(e) => handlePersonalChange("job_title", e.target.value)}
                    placeholder="UX/UI Designer"
                  />
                  {validationErrors.job_title && <p className="error-message">{validationErrors.job_title}</p>}
                </div>
                <div className={`form-group ${validationErrors.email ? 'error' : ''}`}>
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={resumeData?.email || ""}
                    onChange={(e) => handlePersonalChange("email", e.target.value)}
                    placeholder="hello@haitham.dev"
                  />
                  {validationErrors.email && <p className="error-message">{validationErrors.email}</p>}
                </div>
                <div className={`form-group ${validationErrors.phone ? 'error' : ''}`}>
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={resumeData?.phone || ""}
                    onChange={(e) => handlePersonalChange("phone", e.target.value)}
                    placeholder="+555 666 777"
                  />
                  {validationErrors.phone && <p className="error-message">{validationErrors.phone}</p>}
                </div>
                <div className={`form-group ${validationErrors.location ? 'error' : ''}`}>
                  <label>Location</label>
                  <input
                    type="text"
                    value={resumeData?.location || ""}
                    onChange={(e) => handlePersonalChange("location", e.target.value)}
                    placeholder="San Francisco, CA"
                  />
                  {validationErrors.location && <p className="error-message">{validationErrors.location}</p>}
                </div>
                <div className={`form-group ${validationErrors.website ? 'error' : ''}`}>
                  <label>Website</label>
                  <input
                    type="text"
                    value={resumeData?.website || ""}
                    onChange={(e) => handlePersonalChange("website", e.target.value)}
                    placeholder="www.haitham.dev"
                  />
                  {validationErrors.website && <p className="error-message">{validationErrors.website}</p>}
                </div>
                <div className={`form-group ${validationErrors.github ? 'error' : ''}`}>
                  <label>GitHub</label>
                  <input
                    type="text"
                    value={resumeData?.github || ""}
                    onChange={(e) => handlePersonalChange("github", e.target.value)}
                    placeholder="https://github.com/yourusername"
                  />
                  {validationErrors.github && <p className="error-message">{validationErrors.github}</p>}
                </div>
                <div className={`form-group ${validationErrors.linkedin ? 'error' : ''}`}>
                  <label>LinkedIn</label>
                  <input
                    type="text"
                    value={resumeData?.linkedin || ""}
                    onChange={(e) => handlePersonalChange("linkedin", e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                  {validationErrors.linkedin && <p className="error-message">{validationErrors.linkedin}</p>}
                </div>
                <div className={`form-group ${validationErrors.hackerrank ? 'error' : ''}`}>
                  <label>HackerRank</label>
                  <input
                    type="text"
                    value={resumeData?.hackerrank || ""}
                    onChange={(e) => handlePersonalChange("hackerrank", e.target.value)}
                    placeholder="https://www.hackerrank.com/yourusername"
                  />
                  {validationErrors.hackerrank && <p className="error-message">{validationErrors.hackerrank}</p>}
                </div>
                <div className={`form-group ${validationErrors.leetcode ? 'error' : ''}`}>
                  <label>LeetCode</label>
                  <input
                    type="text"
                    value={resumeData?.leetcode || ""}
                    onChange={(e) => handlePersonalChange("leetcode", e.target.value)}
                    placeholder="https://leetcode.com/yourusername"
                  />
                  {validationErrors.leetcode && <p className="error-message">{validationErrors.leetcode}</p>}
                </div>
              </div>
              <WebsiteLinksInput
                websites={resumeData.websites || []}
                newWebsite={newWebsite}
                setNewWebsite={setNewWebsite}
                onAdd={addWebsite}
                onRemove={removeWebsite}
              />
            </div>
          )}
          {activeSection === 'summary' && (
            <div className="tab-panel">
               <h3 className="section-title">Professional Summary</h3>
              <div className={`form-group ${validationErrors.professional_summary ? 'error' : ''}`}>
                <textarea
                  value={resumeData?.professional_summary || ""}
                  onChange={(e) => updateField("professional_summary", e.target.value)}
                  placeholder="Hello, I'm Haitham. A dedicated UI/UX Designer based in San Francisco..."
                  rows={8}

                />
                {validationErrors.professional_summary && <p className="error-message">{validationErrors.professional_summary}</p>}
              </div>
            </div>
          )}
          {activeSection === 'experience' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Experience</h3>
                <AddButton onClick={addExperience}>
                  + Add Experience
                </AddButton>
              </div>
              {(resumeData.experience || []).map((exp, index) => {
                const expErrors = validationErrors.experience?.[index] || {};
                return (
                  <div key={index} className="experience-card">
                    <div className="card-header">
                      <h4>Experience {index + 1}</h4>
                      <RemoveButton onClick={() => removeExperience(index)} ariaLabel={`Remove experience ${index + 1}`} />
                    </div>
                    <div className="card-content">
                      <div className="form-group-grid">
                        <div className={`form-group ${expErrors.position ? 'error' : ''}`}>
                          <label>Position</label>
                          <input type="text" value={exp.position || ""} onChange={(e) => updateExperience(index, "position", e.target.value)} placeholder="UI/UX Designer" maxLength="70"/>
                          {expErrors.position && <p className="error-message">{expErrors.position}</p>}
                        </div>
                        <div className={`form-group ${expErrors.company ? 'error' : ''}`}>
                          <label>Company Name</label>
                          <input type="text" value={exp.company || ""} onChange={(e) => updateExperience(index, "company", e.target.value)} placeholder="Company Name" maxLength="70"/>
                          {expErrors.company && <p className="error-message">{expErrors.company}</p>}
                        </div>
                      </div>
                      <div className="form-group-grid">
                        <div className={`form-group ${expErrors.location ? 'error' : ''}`}>
                          <label>Location</label>
                          <input type="text" value={exp.location || ""} onChange={(e) => updateExperience(index, "location", e.target.value)} placeholder="City, Country" maxLength="80"/>
                          {expErrors.location && <p className="error-message">{expErrors.location}</p>}
                        </div>
                        <div className={`form-group ${expErrors.start_date ? 'error' : ''}`}>
                          <label>Start Date</label>
                          <input type="text" value={exp.start_date || ""} onChange={(e) => updateExperience(index, "start_date", e.target.value)} placeholder="Jan 2019" maxLength="20"/>
                          {expErrors.start_date && <p className="error-message">{expErrors.start_date}</p>}
                        </div>
                        <div className={`form-group ${expErrors.end_date ? 'error' : ''}`}>
                          <label>End Date</label>
                          <input type="text" value={exp.end_date || ""} onChange={(e) => updateExperience(index, "end_date", e.target.value)} placeholder="Mar 2022" maxLength="20"/>
                          {expErrors.end_date && <p className="error-message">{expErrors.end_date}</p>}
                        </div>
                      </div>
                      <div className={`form-group ${expErrors.description_points ? 'error' : ''}`}>
                        <label>Description Points</label>
                        {getDescriptionPoints(exp).map((point, pIndex) => (
                          <div key={pIndex} className="point-input">
                            <textarea value={point} onChange={(e) => updateDescriptionPoint(index, pIndex, e.target.value)} placeholder="Describe your responsibilities..." rows={3} maxLength="300"/>
                            <button onClick={() => removeDescriptionPoint(index, pIndex)} className="remove-point-button">
                              🗑️
                            </button>
                          </div>
                        ))}
                        <button onClick={() => addDescriptionPoint(index)} className="add-point-button">
                          + Add Point
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {activeSection === 'education' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Education</h3>
                <AddButton onClick={addEducation}>
                  + Add Education
                </AddButton>
              </div>
              {(resumeData.education || []).map((edu, index) => {
                 const eduErrors = validationErrors.education?.[index] || {};
                 return (
                  <div key={index} className="education-card">
                    <div className="card-header">
                      <h4>Education {index + 1}</h4>
                      <RemoveButton onClick={() => removeEducation(index)} ariaLabel={`Remove education ${index + 1}`} />
                    </div>
                     <div className="card-content">
                      <div className="form-group-grid">
                        <div className={`form-group ${eduErrors.degree ? 'error' : ''}`}>
                          <label>Degree</label>
                          <input
                            type="text"
                            value={edu.degree || ""}
                            onChange={(e) => updateEducation(index, "degree", e.target.value)}
                            placeholder="Bachelor of Science"
                            maxLength="100"
                          />
                          {eduErrors.degree && <p className="error-message">{eduErrors.degree}</p>}
                        </div>
                        <div className={`form-group ${eduErrors.field_of_study ? 'error' : ''}`}>
                          <label>Field of Study</label>
                          <input
                            type="text"
                            value={edu.field_of_study || ""}
                            onChange={(e) => updateEducation(index, "field_of_study", e.target.value)}
                            placeholder="Computer Science"
                            maxLength="100"
                          />
                          {eduErrors.field_of_study && <p className="error-message">{eduErrors.field_of_study}</p>}
                        </div>
                      </div>
                      <div className="form-group-grid">
                        <div className={`form-group ${eduErrors.institution ? 'error' : ''}`}>
                          <label>Institution</label>
                          <input
                            type="text"
                            value={edu.institution || edu.institute || ""}
                            onChange={(e) => updateEducation(index, "institution", e.target.value)}
                            placeholder="University Name"
                            maxLength="120"
                          />
                          {eduErrors.institution && <p className="error-message">{eduErrors.institution}</p>}
                        </div>
                        <div className={`form-group ${eduErrors.location ? 'error' : ''}`}>
                          <label>Location</label>
                          <input
                            type="text"
                            value={edu.location || ""}
                            onChange={(e) => updateEducation(index, "location", e.target.value)}
                            placeholder="City, Country"
                            maxLength="120"
                          />
                          {eduErrors.location && <p className="error-message">{eduErrors.location}</p>}
                        </div>
                      </div>
                      <div className="form-group-grid">
                        <div className={`form-group ${eduErrors.start_date ? 'error' : ''}`}>
                          <label>Start Date</label>
                          <input
                            type="text"
                            value={edu.start_date || edu.start_year || ""}
                            onChange={(e) => updateEducation(index, "start_date", e.target.value)}
                            placeholder="Sep 2019"
                            maxLength="20"
                          />
                          {eduErrors.start_date && <p className="error-message">{eduErrors.start_date}</p>}
                        </div>
                        <div className={`form-group ${eduErrors.graduation_date ? 'error' : ''}`}>
                          <label>End Date</label>
                          <input
                            type="text"
                            value={edu.graduation_date || edu.end_date || edu.end_year || ""}
                            onChange={(e) => updateEducation(index, "graduation_date", e.target.value)}
                            placeholder="May 2023"
                            maxLength="20"
                          />
                          {eduErrors.graduation_date && <p className="error-message">{eduErrors.graduation_date}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {activeSection === 'skills' && (
            <div className="tab-panel">
              <h3 className="section-title">Skills</h3>
              
              <CategorizedSkillsInput
                skills={resumeData.skills || {}}
                onClear={() => {
                  const clearedSkills = {
                    programming_languages: [],
                    frameworks: [],
                    tools: [],
                    ai_ml_technologies: [],
                  };
                  // Clear both structured skills and legacy key_skills (if present)
                  onUpdate((prev) => ({
                    ...prev,
                    skills: clearedSkills,
                    ...(prev.key_skills ? { key_skills: [] } : {}),
                  }));
                }}
                clearLabel="Clear"
                onChange={(updatedSkills) => {
                  onUpdate((prev) => ({
                    ...prev,
                    skills: updatedSkills,
                    ...(prev.key_skills ? { key_skills: [] } : {}),
                  }));
                }}
                categories={[
                  { key: 'programming_languages', label: 'Programming Languages', placeholder: 'Add a programming language...' },
                  { key: 'frameworks', label: 'Frameworks', placeholder: 'Add a framework...' },
                  { key: 'tools', label: 'Tools', placeholder: 'Add a tool...' },
                  { key: 'ai_ml_technologies', label: 'AI/ML Technologies', placeholder: 'Add an AI/ML technology...' },
                ]}
              />

              {/* Professional Skills (for template defaults only) */}
              {resumeData.isTemplate && (
                <div className="form-group">
                  <label>Professional Skills</label>
                  <div className="skills-list flex flex-wrap gap-2">
                    {(resumeData.professional_skills || []).map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                        <button
                          type="button"
                          className="remove-skill-btn"
                          onClick={() => {
                            const updatedSkills = (resumeData.professional_skills || []).filter((_, i) => i !== index);
                            updateField('professional_skills', updatedSkills);
                          }}
                        >
                          🗑️
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="add-skill-container">
                    <input
                      type="text"
                      placeholder="Add Professional Skill"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          const updatedSkills = [...(resumeData.professional_skills || []), e.target.value.trim()];
                          updateField('professional_skills', updatedSkills);
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="add-skill-btn"
                      onClick={(e) => {
                        const input = e.target.previousSibling;
                        if (input.value.trim()) {
                          const updatedSkills = [...(resumeData.professional_skills || []), input.value.trim()];
                          updateField('professional_skills', updatedSkills);
                          input.value = '';
                        }
                      }}
                    >
                      + Add
                    </button>
                  </div>
                </div>
              )}

              {/* Technical Skills (for template defaults only) */}
              {resumeData.isTemplate && (
                <div className="form-group">
                <label>Technical Skills</label>
                <div className="skills-list flex flex-wrap gap-2">
                  {(resumeData.technical_skills || []).map((skill, index) => (
                    <span key={index} className="skill-tag">
                      {skill}
                      <button
                        type="button"
                        className="remove-skill-btn"
                        onClick={() => {
                          const updatedSkills = (resumeData.technical_skills || []).filter((_, i) => i !== index);
                          updateField('technical_skills', updatedSkills);
                        }}
                      >
                        🗑️
                      </button>
                    </span>
                  ))}
                </div>
                <div className="add-skill-container">
                  <input
                    type="text"
                    placeholder="Add Technical Skill"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const updatedSkills = [...(resumeData.technical_skills || []), e.target.value.trim()];
                        updateField('technical_skills', updatedSkills);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="add-skill-btn"
                    onClick={(e) => {
                      const input = e.target.previousSibling;
                      if (input.value.trim()) {
                        const updatedSkills = [...(resumeData.technical_skills || []), input.value.trim()];
                        updateField('technical_skills', updatedSkills);
                        input.value = '';
                      }
                    }}
                  >
                    + Add
                  </button>
                </div>
              </div>
                )}

            </div>
          )}
          {activeSection === 'achievements' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Achievements</h3>
                <AddButton onClick={addAchievement}>
                  + Add Achievement
                </AddButton>
              </div>
              {(resumeData.achievements || []).map((achievement, index) => (
                <div key={index} className="experience-card">
                  <div className="card-header">
                    <h4>Achievement {index + 1}</h4>
                    <RemoveButton onClick={() => removeAchievement(index)} ariaLabel={`Remove achievement ${index + 1}`} />
                  </div>
                  <div className="card-content">
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={achievement}
                        onChange={(e) => updateAchievement(index, e.target.value)}
                        placeholder="Describe your achievement..."
                        rows="3"
                        maxLength="200"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {activeSection === 'certifications' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">Certifications</h3>
                <AddButton onClick={addCertification}>
                  + Add Certification
                </AddButton>
              </div>
              {(resumeData.certifications || []).map((cert, index) => (
                <div key={index} className="experience-card">
                  <div className="card-header">
                    <h4>Certification {index + 1}</h4>
                    <RemoveButton onClick={() => removeCertification(index)} ariaLabel={`Remove certification ${index + 1}`} />
                  </div>
                  <div className="card-content">
                    <div className="form-group-grid">
                      <div className="form-group">
                        <label>Certification Name</label>
                        <input
                          type="text"
                          value={typeof cert === 'string' ? cert : (cert.name || '')}
                          onChange={(e) => updateCertification(index, 'name', e.target.value)}
                          placeholder="Certification Name"
                          maxLength="100"
                        />
                      </div>
                      <div className="form-group">
                        <label>Provider</label>
                        <input
                          type="text"
                          value={typeof cert === 'object' ? (cert.provider || '') : ''}
                          onChange={(e) => updateCertification(index, 'provider', e.target.value)}
                          placeholder="Certification Provider"
                          maxLength="100"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Languages section removed - now handled by dynamic sections */}
          {activeSection === 'references' && (
            <div className="tab-panel">
              <div className="section-header">
                <h3 className="section-title">References</h3>
                <AddButton onClick={addReference}>
                  + Add Reference
                </AddButton>
              </div>
              {(resumeData.references || []).map((ref, index) => {
                const refErrors = validationErrors.references?.[index] || {};
                return (
                  <div key={index} className="experience-card">
                    <div className="card-header">
                      <h4>Reference {index + 1}</h4>
                      <RemoveButton onClick={() => removeReference(index)} ariaLabel={`Remove reference ${index + 1}`} />
                    </div>
                    <div className="card-content">
                      <div className="form-group-grid">
                        <div className={`form-group ${refErrors.name ? 'error' : ''}`}>
                          <label>Name</label>
                          <input type="text" value={ref.name || ""} onChange={(e) => updateReference(index, "name", e.target.value)} placeholder="John Smith" maxLength="50"/>
                          {refErrors.name && <p className="error-message">{refErrors.name}</p>}
                        </div>
                        <div className={`form-group ${refErrors.title ? 'error' : ''}`}>
                          <label>Title</label>
                          <input type="text" value={ref.title || ""} onChange={(e) => updateReference(index, "title", e.target.value)} placeholder="Senior Manager" maxLength="60"/>
                          {refErrors.title && <p className="error-message">{refErrors.title}</p>}
                        </div>
                      </div>
                      <div className={`form-group ${refErrors.company ? 'error' : ''}`}>
                        <label>Company</label>
                        <input type="text" value={ref.company || ""} onChange={(e) => updateReference(index, "company", e.target.value)} placeholder="Company Name" maxLength="60"/>
                        {refErrors.company && <p className="error-message">{refErrors.company}</p>}
                      </div>
                      <div className="form-group-grid">
                        <div className={`form-group ${refErrors.phone ? 'error' : ''}`}>
                          <label>Phone</label>
                          <input type="text" value={ref.phone || ""} onChange={(e) => updateReference(index, "phone", e.target.value)} placeholder="+555 000 000" maxLength="20"/>
                          {refErrors.phone && <p className="error-message">{refErrors.phone}</p>}
                        </div>
                        <div className={`form-group ${refErrors.email ? 'error' : ''}`}>
                          <label>Email</label>
                          <input type="email" value={ref.email || ""} onChange={(e) => updateReference(index, "email", e.target.value)} placeholder="john@company.com" maxLength="60"/>
                          {refErrors.email && <p className="error-message">{refErrors.email}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {activeSection === 'projects' && (
            <div className="tab-panel">
              <div className="section-header">
              <h3 className="section-title">Projects</h3>
                <AddButton onClick={addProject}>+ Add Project</AddButton>
              </div>
              {(resumeData.projects || []).map((proj, index) => (
                <div key={index} className="experience-card">
                  <div className="card-header">
                    <h4>Project {index + 1}</h4>
                    <RemoveButton onClick={() => removeProject(index)} ariaLabel={`Remove project ${index + 1}`} />
                  </div>
                  <div className="card-content">
              <div className="form-group">
                <label>Title</label>
                      <input
                        type="text"
                        value={proj.title || ''}
                        onChange={(e) => updateProject(index, "title", e.target.value)}
                        placeholder="Project Title"
                        maxLength="100"
                      />
              </div>
              <div className="form-group">
                <label>Description</label>
                      <textarea
                        value={proj.description || ''}
                        onChange={(e) => updateProject(index, "description", e.target.value)}
                        placeholder="A short description of your project."
                        rows={3}
                        maxLength="300"
                      />
              </div>
              <div className="form-group">
                <label>Link</label>
                      <input
                        type="text"
                        value={proj.link || ''}
                        onChange={(e) => updateProject(index, "link", e.target.value)}
                        placeholder="https://github.com/your/project"
                        maxLength="100"
                      />
              </div>
              <div className="form-group">
                <label>Year</label>
                      <input
                        type="text"
                        value={proj.year || ""}
                        onChange={(e) => updateProject(index, "year", e.target.value)}
                        placeholder="2025"
                        maxLength="10"
                      />
              </div>
              <div className="form-group">
                <label>Technologies (comma separated)</label>
                      <input
                        type="text"
                        value={Array.isArray(proj.technologies) ? proj.technologies.join(', ') : (proj.technologies || '')}
                        onChange={(e) => {
                          const technologies = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                          updateProject(index, "technologies", technologies);
                        }}
                        placeholder="React, Node.js, MongoDB"
                        maxLength="200"
                      />
              </div>
              </div>
                  </div>
                ))}
            </div>
          )}

          {/* DYNAMIC SECTIONS - Rendered from ResumeEditor.jsx */}
          {renderDynamicSectionForm && getDynamicSections && getDynamicSections().map(section => {
            if (activeSection === section.id && !baseSections.find(s => s.id === section.id)) {
              return renderDynamicSectionForm(section.id);
            }
            return null;
          })}

        </div>
      </div>
    </div>
  );
};

export default ModernResumeForm;


